import { type PublishedLegend } from '@/features/jobs/legends/PublishedLegend'
import { computed } from 'mobx'
import { task } from 'mobx-task'
import {
  combineLatest,
  from,
  merge,
  NEVER,
  type Observable,
  of,
  firstValueFrom,
} from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  find,
  map,
  scan,
  share,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'
import { fromStream } from '@/utils/mobx-util'
import {
  fromAbortSignal,
  shareBehavior,
  shareLatest,
  whileActive,
  withAbortSignal,
} from '@/utils/rx-util'
import { match } from '@/utils/pattern-match-util'
import type { DocumentRequestsViewModelFactory } from '@/features/documents'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { type Job, type JobAnswer } from '@/features/jobs/Job'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { type QuestionEditorViewModel } from '@/features/jobs/questions/QuestionEditorViewModel'
import { type QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import type { AnswersMap, Question } from '@/features/jobs/questions/questions'
import type { QuestionIconType } from '@/features/jobs/questions/question-icon-type'
import { mergeAnswers } from '@/features/jobs/questions/answers-map'
import { type QuestionStore } from '@/features/jobs/questions/QuestionStore'
import { type AnswersChanged } from './DraftPersister'
import { type DraftStateSlice, type PrepareContext } from './DraftJobViewModel'
import {
  type StepAccessor,
  type StepFactory,
  type StepViewModel,
} from './OutlineViewModel'

/**
 * Draft slice for the legend's intake questions.
 */
export class IntakeQuestionsViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * A step for every question, none while running the first query for a
   * context.
   *
   * @private
   */
  private readonly _questionSteps = new ViewModelList({
    models: () =>
      this.isRunningFirstQuery
        ? []
        : (this.questionList?.questionVms.slice() ?? []),
    create: (question: QuestionEditorViewModel) =>
      new QuestionStepViewModel(this._createStep, this._isReadOnly, question),
  })

  /**
   * When creating a new Draft, we can preselect answers.
   * These always gets set BEFORE the `job$` observable emits, since we can only
   * preselect on new drafts.
   *
   * @private
   */
  private _preselectedAnswers: JobAnswer[] = []

  /**
   * Pieces of infra that are derived from the Job.
   *
   * @private
   */
  private readonly _context$

  /**
   * The questions VM.
   */
  private readonly _questionList$

  /**
   * Stream of answers.
   * Emits latest answers on subscribe.
   *
   * @private
   */
  private readonly _answers$

  /**
   * Changes observable that emits whenever the answers change or when
   * a Legend was updated (this will queue a save of the answers
   * we had collected already).
   *
   * Example:
   *  1. Select Legend1 (Legend change)
   *  2. Answers a few questions (Answers change)
   *  3. Select Legend2 (Legend change)
   *  4. Select Legend 1 (Legend change)
   *  5. The answers from Step 2 are emitted and causes the Job to be saved
   *     with those answers (because changing the Legend clears the answers
   *     on the server).
   */
  readonly changes$

  /**
   * If..
   *  1. The context changed
   *  2. The Legend changed
   *  3. The answers changed
   * Then query for the updated question list.
   *
   * The observable emits a `boolean` of whether the questionnaire is completed.
   *
   * @private
   */
  private readonly _query$

  /**
   * Derivation that tracks whether we are running the first query for the
   * current context. This is used to show a loader but only while the first
   * query is running.
   *
   * @private
   */
  private readonly _isRunningFirstQuery$

  /**
   * Whether all questions have been completed.
   */
  readonly allQuestionsCompleted$

  /**
   * Streams as observables.
   * @private
   */
  private readonly _$

  /**
   * Constructor.
   */
  constructor(
    private readonly _createStep: StepFactory,
    private readonly _createQuestionListViewModel: CreateDraftQuestionListViewModelFn,
    private readonly _createDocumentRequestsViewModel: PromiseLike<DocumentRequestsViewModelFactory>,
    private readonly _questionStore: QuestionStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _isReadOnly: () => boolean,
    private readonly _job$: Observable<Job>,
    private readonly _legendChange$: Observable<PublishedLegend | null>,
  ) {
    this._context$ = this._job$.pipe(
      switchMap((job) => {
        // Creates a fresh context for the job.
        const createContext = async (): Promise<IntakeQuestionsContext> => ({
          job,
          list: this._createQuestionListViewModel(
            job,
            await this._createDocumentRequestsViewModel,
          ),
        })

        // Create a context for the current state of the job, then for every
        // Legend change: the no-op context when the Legend is cleared, and a new
        // context when one is selected.
        return this._legendChange$.pipe(
          map((legend) => legend !== null),
          startWith(true),
          switchMap((hasLegend) =>
            hasLegend ? from(createContext()) : of(null),
          ),
        )
      }),
      distinctUntilChanged(),
      shareLatest(),
    )

    this._questionList$ = this._context$.pipe(map((ctx) => ctx?.list ?? null))

    this._answers$ = this._job$.pipe(
      // When we have a Job instance...
      switchMap((job) => {
        // Preselected and stored answers carry no question type, so they
        // can't be sent; they only tell whether there are answers to keep.
        const initial: IntakeAnswers = {
          answers: null,
          isPristine:
            (this._maybeConsumePreselectedAnswers() ?? job.answers).length ===
            0,
        }

        // Then emit whenever the answers have changed.
        return this._questionList$.pipe(
          switchMap((list) =>
            list
              ? list.answersChanged$.pipe(
                  map((answers) => ({
                    answers,
                    askedIds: list.questionVms.map((vm) => vm.id),
                  })),
                )
              : NEVER,
          ),
          // This lets us keep answers that are not relevant anymore but
          // in a way that won't alter the behavior of answering questions.
          // If we change paths (or even legends!), then change back, we
          // will remember the answers.
          scan(
            (previous: IntakeAnswers, { answers, askedIds }) => ({
              answers: previous.answers
                ? mergeAnswers(previous.answers, answers, askedIds)
                : answers,
              isPristine: false,
            }),
            initial,
          ),
          startWith(initial),
        )
      }),
      // Anytime someone subscribes, emit the latest answers we have. Stays
      // connected while inactive, so answers given before navigating away are
      // still merged in when coming back.
      shareReplay(1),
    )

    this.changes$ = merge(
      // Skip the first emission since that will be from a load.
      this._answers$.pipe(skip(1)),
      // When the Legend changes, take the first emission from the answers stream.
      this._legendChange$.pipe(
        filter((legend) => legend !== null),
        switchMap(() => this._answers$.pipe(take(1))),
      ),
    ).pipe(
      map(({ answers, isPristine }): AnswersChanged => ({
        // Clearing everything is only meant when there never were answers;
        // otherwise the answers we can't send would be wiped.
        answers: answers && (isPristine || answers.size > 0) ? answers : null,
        type: 'AnswersChanged',
      })),
      share(),
    )

    this._query$ = this._context$.pipe(
      switchMap((ctx) => combineLatest([of(ctx), this._answers$])),
      switchMap(([ctx, answers]) =>
        // Any time we have a set of answers to provide to the server,
        // send a query. If we have no context, cancel the previous query if any.
        ctx === null
          ? NEVER
          : withAbortSignal((signal) =>
              this._queryQuestions(ctx, answers.answers, signal),
            ),
      ),
      whileActive(this._lifecycle.active$),
      share(),
    )

    this._isRunningFirstQuery$ = this._context$.pipe(
      switchMap((ctx) =>
        ctx === null
          ? of(false)
          : merge(
              // Immediately when we get a new context, signal that the first query
              // is running.
              of(true),
              // When the next query completes, signal that the first query is
              // no longer running
              this._query$.pipe(
                take(1),
                map(() => false),
              ),
            ),
      ),
      distinctUntilChanged(),
      shareBehavior(false),
    )

    this.allQuestionsCompleted$ = combineLatest([
      this._context$,
      this._query$,
      this._isRunningFirstQuery$,
    ]).pipe(
      map(
        ([ctx, completed, runningFirst]) =>
          ctx !== null && !runningFirst && completed,
      ),
      distinctUntilChanged(),
      shareBehavior<boolean>(false),
    )

    this._$ = {
      questionList: fromStream<QuestionListViewModel | null>(
        null,
        this._questionList$,
      ),
      isRunningFirstQuery: fromStream(false, this._isRunningFirstQuery$),
      allQuestionsCompleted: fromStream(false, this.allQuestionsCompleted$),
    }

    this._lifecycle.manage(this._query$)
    this._lifecycle.manage(this._questionSteps)
  }

  /**
   * Step accessors for this slice.
   */
  @computed
  get stepAccessors(): QuestionStepViewModel[] {
    return this._questionSteps.items
  }

  /**
   * Gets the Questions.
   */
  @computed
  get questionList() {
    return this._$.questionList()
  }

  /**
   * MobX getter for the observable of the same name.
   */
  @computed
  get isRunningFirstQuery() {
    return this._$.isRunningFirstQuery()
  }

  /**
   * Whether we are currently querying.
   */
  @computed
  get querying() {
    return this._queryQuestions.pending
  }

  /**
   * Whether the questionnaire is completed.
   */
  @computed
  get completed() {
    return this._$.allQuestionsCompleted()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Prepares this slice.
   *
   * @param context
   * @param signal
   */
  async prepare(context: PrepareContext, signal?: AbortSignal) {
    this._preselectedAnswers = []
    if (context.type === 'EditDraft') {
      // Wait for the first questions query to complete
      // if we have a job. This prevents flickering of the
      // main view and the outline.
      await this.waitForNextQuery(signal)
    } else {
      this._preselectedAnswers = context.prefilledDraft.answers
    }
  }

  /**
   * Waits for the next query to complete (if one is queued).
   *
   * @param signal If aborted, returns immediately.
   */
  waitForNextQuery(signal?: AbortSignal) {
    return firstValueFrom(
      this._isRunningFirstQuery$.pipe(
        find((v) => !v),
        takeUntil(fromAbortSignal(signal)),
      ),
      { defaultValue: false },
    )
  }

  /**
   * Queries questions
   */
  private _queryQuestions = task.resolved(
    async (
      context: IntakeQuestionsContext,
      answers: AnswersMap | null,
      signal: AbortSignal,
    ): Promise<boolean> => {
      try {
        const result = await this._createAlertRetryWrapper(
          'Loading the questions failed',
          signal,
        )(() =>
          this._questionStore.fetchQuestions({
            jobId: context.job.id,
            answers,
            signal,
          }),
        )
        signal.throwIfAborted()
        context.list.reconcile(result.questions)
        return result.intakeCompleted
      } catch {
        return false
      }
    },
  )

  /**
   * If there are preselected answers, consumes them by returning them and
   * clearing the backing field.
   *
   * @private
   */
  private _maybeConsumePreselectedAnswers() {
    if (this._preselectedAnswers.length === 0) {
      return null
    }

    const answers = this._preselectedAnswers
    this._preselectedAnswers = []
    return answers
  }
}

/**
 * Context object used for scoping state to a specific job.
 */
interface IntakeQuestionsContext {
  job: Job
  list: QuestionListViewModel
}

/**
 * The answers collected for a draft.
 */
interface IntakeAnswers {
  /**
   * `null` until answered, so that the questions follow from the job's own
   * answers.
   */
  answers: AnswersMap | null
  /**
   * Whether there never were any answers, not even ones that can't be sent.
   */
  isPristine: boolean
}

/**
 * Question step.
 */
export class QuestionStepViewModel implements StepAccessor {
  readonly step: StepViewModel

  constructor(
    createStep: StepFactory,
    private readonly _isDisabled: () => boolean,
    readonly question: QuestionEditorViewModel,
  ) {
    this.step = createStep({
      stepId: `question_${question.id.toLowerCase()}`,
      isShortCircuited: () => question.editor.type === 'Breaker',
      isCompleted: () => question.editor.validate().type === 'Success',
      outlineItemDisplay: () => ({
        title: question.title,
        value: question.editor.isAnswered
          ? question.editor.textAnswer
          : undefined,
        shortValue: question.editor.isAnswered
          ? question.editor.truncatedTextAnswer(100)
          : undefined,
        visual: question.editor.isAnswered
          ? {
              type: 'QuestionIcon',
              questionType: getQuestionIconType(question.editor.type),
            }
          : undefined,
        description: question.description,
      }),
    })
  }

  /**
   * Whether the step is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * Whether the question still needs an answer. Breakers and documents never
   * count as incomplete.
   */
  @computed
  get isIncomplete() {
    return (
      this.question.editor.type !== 'Breaker' &&
      this.question.editor.type !== 'Documents' &&
      !this.step.isCompleted
    )
  }
}

/**
 * Creates the question list for a draft's intake questions.
 */
export type CreateDraftQuestionListViewModelFn = (
  job: Job,
  createDocumentRequestsViewModel: DocumentRequestsViewModelFactory,
) => QuestionListViewModel

/**
 * Factory for creating an {@link IntakeQuestionsViewModel}.
 */
export type IntakeQuestionsViewModelFactory = ReturnType<
  typeof createIntakeQuestionsViewModelFactory
>

/** @see IntakeQuestionsViewModelFactory */
export function createIntakeQuestionsViewModelFactory(
  createQuestionListViewModel: CreateDraftQuestionListViewModelFn,
  createDocumentRequestsViewModel: PromiseLike<DocumentRequestsViewModelFactory>,
  questionStore: QuestionStore,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
) {
  return (
    createStep: StepFactory,
    isReadOnly: () => boolean,
    job$: Observable<Job>,
    legendChange$: Observable<PublishedLegend | null>,
  ) =>
    new IntakeQuestionsViewModel(
      createStep,
      createQuestionListViewModel,
      createDocumentRequestsViewModel,
      questionStore,
      createAlertRetryWrapper,
      isReadOnly,
      job$,
      legendChange$,
    )
}

function getQuestionIconType(type: Question['type']): QuestionIconType | null {
  return match(type, {
    Bool: 'yes-no',
    Text: 'text',
    Numeric: 'numeric',
    DateTime: 'date-time',
    Documents: 'document-upload',
    Select: 'choice',
    Breaker: 'breaker',
    Unknown: null,
  })
}
